import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import PushPinIcon from '@mui/icons-material/PushPin';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CategoryIcon from '@mui/icons-material/Category';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import useApiService from '../../component/hooks/useApiService';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import { useDialog } from '../../context/dialog/DialogContext';
import AnnouncementForm from './AnnouncementForm';
import {
  createAnnouncement,
  listAdminAnnouncements,
  listAnnouncementCategories,
  updateAnnouncementCategory,
  deleteAnnouncementCategory,
  createAnnouncementCategory,
  updateAnnouncement,
} from '../../services/announcements';

const statusColor = {
  scheduled: 'warning',
  published: 'success',
  archived: 'default',
  archieved: 'default', // legacy typo from backend data
};

const statusLabel = {
  scheduled: 'Zamanlandı',
  published: 'Yayında',
  archived: 'Arşivde',
  archieved: 'Arşivde', // legacy typo
};

const behaviorLabel = {
  external: 'Dış Link',
  none: 'Tıklama Yok',
};

const extractItems = (res) => res?.items || res?.data?.items || res?.data || res || [];

export default function AnnouncementList() {
  const { executeGet, executePost, executePatch, executeDelete } = useApiService();
  const { showError, showSuccess } = useAppSnackbar() ?? {};
  const { openDialog } = useDialog();

  const getRef = useRef(executeGet);
  const postRef = useRef(executePost);
  const patchRef = useRef(executePatch);
  const deleteRef = useRef(executeDelete);
  useEffect(() => { getRef.current = executeGet; }, [executeGet]);
  useEffect(() => { postRef.current = executePost; }, [executePost]);
  useEffect(() => { patchRef.current = executePatch; }, [executePatch]);
  useEffect(() => { deleteRef.current = executeDelete; }, [executeDelete]);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    status: '',
    category: '',
    pinned: '',
    q: '',
    from: null,
  });

  const loadCategories = useCallback(async () => {
    try {
      const res = await listAnnouncementCategories(getRef.current);
      setCategories(res?.items || res?.data?.items || []);
    } catch (e) {
      console.error('Kategori çekilemedi', e);
      setCategories([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.pinned !== '') params.pinned = filters.pinned === 'true';
      if (filters.q) params.q = filters.q;
      if (filters.from) params.from = dayjs(filters.from).toISOString();

      const res = await listAdminAnnouncements(getRef.current, params);
      const list = extractItems(res) || [];
      setItems(list);
    } catch (e) {
      console.error('Announcements fetch failed', e);
      showError?.('Duyurular yüklenemedi.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (field) => (e) => {
    const value = e?.target?.value;
    setFilters((cur) => ({ ...cur, [field]: value }));
  };

  const handleDateChange = (field) => (value) => {
    setFilters((cur) => ({ ...cur, [field]: value }));
  };

  const openForm = (item) => {
    const dialog = openDialog({
      title: item ? 'Duyuru Düzenle' : 'Yeni Duyuru',
      content: (
        <AnnouncementForm
          categories={categories}
          initialData={item}
          onSubmit={async (payload) => {
            if (item?.id) {
              await updateAnnouncement(patchRef.current, item.id, payload);
              showSuccess?.('Güncellendi');
            } else {
              await createAnnouncement(postRef.current, payload);
              showSuccess?.('Oluşturuldu');
            }
            loadData();
            dialog.close();
          }}
          onCancel={() => dialog.close()}
        />
      ),
      callback: () => {},
      maxWidth: 'md',
    });
  };

  const renderStatus = (value) => (
    <Chip label={statusLabel[value] || value} color={statusColor[value] || 'default'} size="small" />
  );

  const renderPinned = (val) =>
    val ? <Chip label="Sabit" size="small" icon={<PushPinIcon fontSize="small" />} /> : '-';

  const renderBehavior = (val) => (
    <Chip label={behaviorLabel[val] || val} size="small" variant="outlined" />
  );

  const rows = useMemo(() => items || [], [items]);

  const openCategoryManager = () => {
    const dialog = openDialog({
      title: 'Kategoriler',
      content: (
        <CategoryManager
          categories={categories}
          onChange={async () => {
            await loadCategories();
            await loadData();
          }}
          showError={showError}
          showSuccess={showSuccess}
          close={() => dialog.close()}
          createFn={(payload) => createAnnouncementCategory(postRef.current, payload)}
          updateFn={(id, payload) => updateAnnouncementCategory(patchRef.current, id, payload)}
          deleteFn={(id) => deleteAnnouncementCategory(deleteRef.current, id)}
        />
      ),
      callback: () => {},
      maxWidth: 'md',
    });
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
        <Typography variant="h5" fontWeight={600}>Duyurular</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<CategoryIcon />} onClick={openCategoryManager}>
            Kategoriler
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>
            Yeni Duyuru
          </Button>
        </Stack>
      </Stack>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TextField
            label="Başlık"
            value={filters.q}
            onChange={handleFilterChange('q')}
            fullWidth
            placeholder="Ara"
            size="small"
          />
          <TextField
            select
            label="Durum"
            value={filters.status}
            onChange={handleFilterChange('status')}
            fullWidth
            size="small"
          >
            <MenuItem value="">Hepsi</MenuItem>
            <MenuItem value="scheduled">Zamanlandı</MenuItem>
            <MenuItem value="published">Yayında</MenuItem>
            <MenuItem value="archived">Arşivde</MenuItem>
          </TextField>
          <TextField
            select
            label="Kategori"
            value={filters.category}
            onChange={handleFilterChange('category')}
            fullWidth
            size="small"
          >
            <MenuItem value="">Hepsi</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.slug}>{c.name}</MenuItem>
            ))}
          </TextField>
          <DatePicker
            label="Yayın Tarihi"
            value={filters.from}
            onChange={handleDateChange('from')}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
          <TextField
            select
            label="Sabit"
            value={filters.pinned}
            onChange={handleFilterChange('pinned')}
            fullWidth
            size="small"
          >
            <MenuItem value="">Hepsi</MenuItem>
            <MenuItem value="true">Sabit</MenuItem>
            <MenuItem value="false">Değil</MenuItem>
          </TextField>
        </LocalizationProvider>
      </Paper>

      <Paper>
        {loading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Yayın Tarihi</TableCell>
                  <TableCell>Sabit</TableCell>
                  <TableCell>Tıklama</TableCell>
                  <TableCell>Bağlantı</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={600}>{row.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{row.slug}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.category?.name || '-'}</TableCell>
                    <TableCell>{renderStatus(row.status)}</TableCell>
                    <TableCell>{row.publish_at ? dayjs(row.publish_at).format('YYYY-MM-DD HH:mm') : '-'}</TableCell>
                    <TableCell>{renderPinned(row.pinned)}</TableCell>
                    <TableCell>{renderBehavior(row.click_behavior)}</TableCell>
                    <TableCell>
                      {row.link_url ? (
                        <Tooltip title={row.link_url}>
                          <IconButton size="small" component="a" href={row.link_url} target="_blank" rel="noreferrer">
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <LinkOffIcon fontSize="small" color="disabled" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Düzenle">
                        <IconButton size="small" onClick={() => openForm(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">Kayıt bulunamadı.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

function CategoryManager({ categories = [], onChange, showError, showSuccess, close, createFn, updateFn, deleteFn }) {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', slug: '', sort_order: '' });
  const [creating, setCreating] = useState({ name: '', slug: '', sort_order: '' });

  useEffect(() => {
    setRows(categories);
    setEditingId(null);
  }, [categories]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      name: row.name || '',
      slug: row.slug || '',
      sort_order: row.sort_order ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ name: '', slug: '', sort_order: '' });
  };

  const saveEdit = async (rowId) => {
    if (!draft.name.trim()) {
      showError?.('Ad zorunlu');
      return;
    }
    const sortNum = draft.sort_order === '' ? undefined : Number(draft.sort_order);
    if (Number.isNaN(sortNum)) {
      showError?.('Sıra sayısal olmalı');
      return;
    }
    if (
      sortNum !== undefined &&
      rows.some((r) => r.id !== rowId && r.sort_order === sortNum)
    ) {
      showError?.('Bu sıra değeri başka kategoride kullanılıyor');
      return;
    }
    try {
      await updateFn(rowId, {
        name: draft.name.trim(),
        slug: draft.slug?.trim() || undefined,
        sort_order: sortNum,
      });
      showSuccess?.('Kategori güncellendi');
      await onChange?.();
      setRows((cur) =>
        cur.map((r) =>
          r.id === rowId ? { ...r, name: draft.name.trim(), slug: draft.slug?.trim() || '', sort_order: sortNum } : r
        )
      );
    } catch (e) {
      console.error(e);
      showError?.('Kategori güncellenemedi');
    } finally {
      cancelEdit();
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`"${row.name}" kategorisi silinsin mi?`)) return;
    try {
      await deleteFn(row.id);
      showSuccess?.('Kategori silindi');
      await onChange?.();
    } catch (e) {
      console.error(e);
      showError?.('Kategori silinemedi');
    }
  };

  const addNew = async () => {
    if (!creating.name.trim()) {
      showError?.('Kategori adı zorunlu');
      return;
    }
    const sortNum = creating.sort_order === '' ? undefined : Number(creating.sort_order);
    if (Number.isNaN(sortNum)) {
      showError?.('Sıra sayısal olmalı');
      return;
    }
    if (sortNum !== undefined && rows.some((r) => r.sort_order === sortNum)) {
      showError?.('Bu sıra değeri başka kategoride kullanılıyor');
      return;
    }
    try {
      await createFn({
        name: creating.name.trim(),
        slug: creating.slug?.trim() || undefined,
        sort_order: sortNum,
      });
      showSuccess?.('Kategori eklendi');
      setCreating({ name: '', slug: '', sort_order: '' });
      await onChange?.();
      setRows((cur) => [...cur, { id: Math.random(), name: creating.name.trim(), slug: creating.slug?.trim(), sort_order: sortNum }]);
    } catch (e) {
      console.error(e);
      showError?.('Kategori eklenemedi');
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Silinen kategorilerdeki duyurular kategori bilgisini kaybeder.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ad</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Sıra</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isEditing = editingId === row.id;
              return (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ width: '40%' }}>
                    {isEditing ? (
                      <TextField
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        size="small"
                        fullWidth
                        placeholder="Kategori adı"
                      />
                    ) : (
                      <Typography fontWeight={600}>{row.name}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '30%' }}>
                    {isEditing ? (
                      <TextField
                        value={draft.slug}
                        onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))}
                        size="small"
                        fullWidth
                        placeholder="slug"
                      />
                    ) : (
                      <Typography color="text.secondary">{row.slug || '-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    {isEditing ? (
                      <TextField
                        value={draft.sort_order}
                        onChange={(e) => setDraft((d) => ({ ...d, sort_order: e.target.value }))}
                        size="small"
                        fullWidth
                        type="number"
                      />
                    ) : (
                      <Typography color="text.secondary">{row.sort_order ?? '-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ width: '15%' }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {isEditing ? (
                        <>
                          <Button size="small" variant="outlined" onClick={() => saveEdit(row.id)}>Kaydet</Button>
                          <Button size="small" onClick={cancelEdit}>Vazgeç</Button>
                        </>
                      ) : (
                        <>
                          <Button size="small" variant="outlined" onClick={() => startEdit(row)}>Düzenle</Button>
                          <Button size="small" color="error" onClick={() => removeRow(row)}>Sil</Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography align="center" color="text.secondary">Kategori bulunamadı.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      <Typography variant="subtitle2">Yeni Kategori</Typography>
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Ad"
              value={creating.name}
              onChange={(e) => setCreating((c) => ({ ...c, name: e.target.value }))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Slug (opsiyonel)"
              value={creating.slug}
              onChange={(e) => setCreating((c) => ({ ...c, slug: e.target.value }))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Sıra"
              value={creating.sort_order}
              onChange={(e) => setCreating((c) => ({ ...c, sort_order: e.target.value }))}
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="contained" size="small" onClick={addNew}>Ekle</Button>
              <Button variant="text" size="small" onClick={() => close?.()}>Kapat</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
}
